import { formatDate } from "common/utils/dates";
import { SaveStatus } from "common/utils/types";
import type { ReactNode } from "react";
import React, { useRef } from "react";
import cx from "ui/cx";
import IconOffline from "ui/icons/IconOffline";
import IconSaved from "ui/icons/IconSaved";
import IconSaving from "ui/icons/IconSaving";
import IconErrorStop from "ui/icons/status/IconErrorStop";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./SaveStatusIndicator.module.scss";

export interface SaveStatusIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  status: SaveStatus;
  savedAt: Date;
}

export default function SaveStatusIndicator({ status, savedAt, className, ...props }: SaveStatusIndicatorProps) {
  const saveStatusRef = useRef<HTMLDivElement>(null);

  let icon: ReactNode;
  let text: string | undefined;
  let tooltipText: string | undefined;

  switch (status) {
    case SaveStatus.SAVED: {
      icon = <IconSaved />;
      text = "Saved to Cloud";
      tooltipText = savedAt ? `Saved at ${formatDate(savedAt, "WITH_TIME")}` : text;
      break;
    }
    case SaveStatus.PRE_SAVE:
    case SaveStatus.SAVING: {
      icon = <IconSaving />;
      text = "Saving...";
      tooltipText = "Saving to Cloud...";
      break;
    }
    case SaveStatus.ERROR: {
      icon = <IconErrorStop />;
      text = "Failed to Save";
      tooltipText = "An error occurred while saving. Refresh the page and try again.";
      break;
    }
    case SaveStatus.OFFLINE: {
      icon = <IconOffline />;
      text = "Offline";
      tooltipText = "You are currently offline. Your changes will sync when you go back online.";
    }
    case SaveStatus.CONFLICT: {
      // Handled independently
      break;
    }
  }

  return (
    <div ref={saveStatusRef} className={cx(styles.wrapper, className)} {...props}>
      {tooltipText && (
        <Tooltip.Root>
          <Tooltip.Trigger>
            <div className={styles.tooltipWrapper}>
              {icon}
              <span
                className={status === SaveStatus.ERROR ? "error" : undefined}
                data-testid="save-status-indicator-text"
              >
                {text}
              </span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Content className={styles.tooltipContent}>{tooltipText}</Tooltip.Content>
        </Tooltip.Root>
      )}
    </div>
  );
}
